import {
	type CustomerInfo,
	type Offerings,
	type Package,
	Purchases as PurchasesWeb,
} from "@revenuecat/purchases-js";

/**
 * RevenueCat Web Service
 * Handles RevenueCat operations for web platform (Stripe integration)
 */

let isConfigured = false;

/**
 * Configure Web SDK with Stripe
 */
export async function configure(userId: string): Promise<void> {
	// Check if already configured by checking if instance exists
	try {
		const instance = PurchasesWeb.getSharedInstance();
		if (instance) {
			isConfigured = true;
			console.log("[RevenueCat Web] Already configured, skipping");
			return;
		}
	} catch {
		// Instance doesn't exist yet, continue with configuration
	}

	if (!userId) {
		throw new Error("User ID is required for Web SDK configuration");
	}

	const apiKey = import.meta.env.VITE_REVENUECAT_WEB_API_KEY;
	if (!apiKey) {
		throw new Error("RevenueCat Web API key not found");
	}

	await PurchasesWeb.configure({
		apiKey,
		appUserId: userId,
	});

	isConfigured = true;
	console.log("[RevenueCat Web] Configured successfully");
}

/**
 * Get available offerings
 */
export async function getOfferings(): Promise<Offerings> {
	if (!isConfigured) {
		console.warn(
			"[RevenueCat Web] SDK not configured yet, offerings unavailable (using fallback)",
		);
		// Return empty offerings instead of throwing
		// The SDK will eventually configure and offerings will be available
		return {
			all: {},
			current: null,
		} as Offerings;
	}

	const instance = PurchasesWeb.getSharedInstance();
	const offerings = await instance.getOfferings();
	return offerings;
}

/**
 * Purchase a package via Stripe
 */
export async function purchasePackage(
	packageToPurchase: Package,
): Promise<{ customerInfo: CustomerInfo; success: boolean }> {
	if (!isConfigured) {
		throw new Error("RevenueCat Web SDK not configured");
	}

	// Validate package structure
	if (!packageToPurchase.rcBillingProduct) {
		throw new Error(
			"Invalid package: Missing rcBillingProduct. Please ensure RevenueCat offerings are properly configured.",
		);
	}

	const result = await PurchasesWeb.getSharedInstance().purchase({
		rcPackage: packageToPurchase,
	});

	console.log("[RevenueCat Web] Purchase successful");
	return { customerInfo: result.customerInfo, success: true };
}

/**
 * Get customer info
 */
export async function getCustomerInfo(): Promise<CustomerInfo> {
	if (!isConfigured) {
		console.warn(
			"[RevenueCat Web] SDK not configured yet, customer info unavailable",
		);
		// Return minimal customer info instead of throwing
		return {
			entitlements: {
				active: {},
				all: {},
			},
			activeSubscriptions: [],
			allPurchasedProductIdentifiers: [],
			latestExpirationDate: null,
			firstSeen: new Date().toISOString(),
			originalAppUserId: "",
			requestDate: new Date().toISOString(),
			allExpirationDates: {},
			allPurchaseDates: {},
			allExpirationDatesByProduct: {},
			allPurchaseDatesByProduct: {},
			firstSeenDate: new Date().toISOString(),
			subscriptionsByProductIdentifier: {},
			originalApplicationVersion: null,
			originalPurchaseDate: null,
			managementURL: null,
			nonSubscriptionTransactions: [],
		} as unknown as CustomerInfo;
	}

	const customerInfo = await PurchasesWeb.getSharedInstance().getCustomerInfo();
	return customerInfo;
}

/**
 * Check if user has active subscription
 */
export async function hasActiveSubscription(): Promise<boolean> {
	try {
		const customerInfo = await getCustomerInfo();
		const activeEntitlements = Object.keys(
			customerInfo.entitlements.active || {},
		);
		return activeEntitlements.length > 0;
	} catch (error) {
		console.error(
			"[RevenueCat Web] Failed to check subscription status:",
			error,
		);
		return false;
	}
}

/**
 * Reset configuration state (for logout)
 */
export function reset(): void {
	isConfigured = false;
}

/**
 * Check if SDK is configured
 */
export function getIsConfigured(): boolean {
	return isConfigured;
}

/**
 * Get subscription management URL
 * For web, this typically returns the Stripe billing portal URL
 */
export async function getManagementURL(): Promise<string | null> {
	if (!isConfigured) {
		return null;
	}

	const customerInfo = await getCustomerInfo();
	return customerInfo.managementURL || null;
}
